import { observable, action } from "mobx";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { Development } from "modules/development/models/Index";
import { DevelopmentService } from "modules/development/DevelopmentService";
import { LoginStore } from "modules/login/LoginStore";
import { LoginService } from "modules/login/LoginService";
import { User } from "modules/login/models/User";
import { CostPlanStore } from "modules/costPlan/CostPlanStore";
import * as _ from "lodash";

enum DevelopmentTabs {
  PROJECT = 0,
  FAVORITES = 1,
}

type TSelectDev = FirebaseDocument<Development> | false;

/**
 * Store de Projetos
 * @category Developments
 * @hideconstructor
 */
class DevelopmentStore {
  /** Aba atual do Projeto. */
  currentTab: DevelopmentTabs = 0;
  /** Valor usado na pesquisa. */
  search: string = "".toLowerCase();
  /** Lista de projetos do pertencentes ou compartilhados com o usuário. */
  @observable developments: FirebaseDocument<Development>[] = [];
  /** Lista de projetos que atendem ao termo pesquisado. */
  developmentsSearch: FirebaseDocument<Development>[] = [];
  /** Lista de projetos favoritados. */
  @observable developmentsFavorites: FirebaseDocument<Development>[] = [];
  /** Lista de projetos favoritados que atendem ao termo pesquisado. */
  developmentsFavoritesSearch: FirebaseDocument<Development>[] = [];
  /** Lista de projetos exibidos na página. */
  @observable developmentsView: FirebaseDocument<Development>[] = [];
  /** Projeto selecionado. */
  @observable developmentSelected: FirebaseDocument<Development>;
  /** ID do projeto selecionado. */
  @observable developmentId: string = "";
  /** Nome do projeto selecionado. */
  @observable developmentName: string = "";
  /** Dados do usuário atual que será utilizado para comparação ao obter os todos os usuários com acesso a um projeto. */
  @observable currentUser: any = undefined;
  /** Dados do usuário proprietário do projeto selecionado. */
  private devSelectedOwner: User | undefined;
  /** Flag que indica se o formulário de novo projeot está aberto. */
  @observable layerAdd: boolean = false;
  /** Flag que indica que se o layer de adicionar projeto foi carregado pela primeira vez. GAMBIARRA */
  @observable firstLoad: boolean = false;
  /** Projeto selecionado para editar o endereço. */
  @observable isEditAddress: TSelectDev = false;
  /** Referência ao elemento do input para imagem de capa do projeto. */
  buttonFileUpload: any = null;
  /** Projeto selecionado para trocar a imagem de capa. */
  updateImageDevelopment: TSelectDev = false;
  /** Flag que indica se o modal de compartilhar projeto está aberto. */
  @observable openShareDevelopmentDialog: boolean = false;
  /** Projeto selecionado para ser compartilhado. */
  @observable openShareDevelopment: TSelectDev = false;
  /** Lista de promesas para obter os dados de usuários compartilhados. GAMBIARRA */
  @observable shareUsers: Array<User | undefined> = [];
  /** Objeto que armazena os dados de usuários compartilhados por projeto. */
  @observable shareUsersByDev: { [devId: string]: string } = {};
  /** Flag que indica se o modal de copiar projeto está aberto.
   * @depreciated
   */
  @observable openCreateDevelopmentDialog: boolean = false;
  /** Flag que indica que o projeto está sendo copiado. */
  @observable loadingCopy: boolean = false;
  /** Projeto selecionado para ser copiado. */
  @observable fromCopy?: FirebaseDocument<Development> = undefined;
  /** Nome do projeto sendo refatorado. */
  @observable isCopy: boolean = false;
  /** Projeto selecionado para ser deletado. */
  @observable deleteDevelopment: TSelectDev = false;
  /** Projeto selecionado para ser desvinculado. */
  @observable removAccessDevelopment: TSelectDev = false;
  /** Flag que indica se é preciso atualizar os valores da EAP. */
  @observable needUpdateEapValues: boolean = false;
  /** Flag que indica se a EAP está sendo aberta pela primeira vez. */
  @observable firstTimeOpenEAP: boolean = true;
  /** Flag que indica se a tabela da composição está sendo aberta pela primeira vez. */
  @observable firstTimeOpenTaskTable: boolean = true;
  /** ID do projeto que será aberto. */
  @observable redirectId: string = "";
  /** Nome do projeto copiado/criado. */
  projectName: string = "";
  /** Flag que indica que o projeto está sendo carregando. */
  @observable loading: boolean = false;
  /** Flag que indica que o projeto está sendo refatorado.
   * @depreciated
   */
  @observable loadingWbsUpdate: boolean = false;
  /** Nome do projeto sendo refatorado. */
  refactorDevelopName: string = "";
  /** Flag que indica que os insumos do projeto precisam ser atualizados. */
  updateResources: boolean = false;
  /** ID do projeto refatorado completamente.
   * @depreciated
   */
  @observable refactorComplete: string = "";

  @observable trialMessageError: string = "";

  resetStore() {
    this.search = "";
    this.currentTab = 0;
    this.developmentsFavorites = [];
    this.developmentsFavoritesSearch = [];
    this.buttonFileUpload = null;
    this.updateImageDevelopment = false;
    this.openShareDevelopmentDialog = false;
    this.openCreateDevelopmentDialog = false;
    this.layerAdd = false;
    this.developmentSelected = undefined as any;
    this.devSelectedOwner = undefined as any;
    this.deleteDevelopment = false;
    this.removAccessDevelopment = false;
    this.firstTimeOpenEAP = true;
    this.firstTimeOpenTaskTable = true;
    this.fromCopy = undefined;
    this.isCopy = false;
    this.isEditAddress = false;
    this.firstLoad = false;
    this.loadingWbsUpdate = false;
    this.refactorComplete = "";
    this.trialMessageError = "";
    CostPlanStore.arrayObjBudgets = [];
  }

  @action
  async setDevelopmentSelected(id): Promise<void> {
    this.developmentId = id;
    await DevelopmentService.setDevelopmentSelected(id);
  }

  async saveUsers(users) {
    DevelopmentService.updateSharedDatabase(users);
  }

  async getDevelomentsOwner(): Promise<User | undefined> {
    if (!this.devSelectedOwner) {
      const dev = this.developmentSelected;
      const usersList = dev.data.users || {};
      const userUid = Object.keys(usersList).find(
        (uid) => usersList[uid] === 0
      );

      if (!userUid) this.devSelectedOwner = undefined;
      else this.devSelectedOwner = await LoginService.fetchUserByID(userUid);
    }

    return this.devSelectedOwner;
  }

  @action
  async setFavorite(id) {
    await DevelopmentService.setFavorite(id);
    let dev = this.developments.find((item) => item.id === id) as any;
    if (dev) {
      dev.data.favorite = true;
      dev = this.developmentsSearch.find((item) => item.id === id) as any;
      if (dev) dev.data.favorite = true;
    }
    this.developmentsFavorites.push(dev);
    if (dev.data.name.includes(this.search))
      this.developmentsFavoritesSearch.push(dev);
    if (this.currentTab === 0) this.developmentsView = this.developmentsSearch;
    else this.developmentsView = this.developmentsFavoritesSearch;
  }

  @action
  async unsetFavorite(id) {
    await DevelopmentService.unsetFavorite(id);
    let dev = this.developments.find((item) => item.id === id) as any;
    if (dev) {
      dev.data.favorite = false;
      dev = this.developmentsSearch.find((item) => item.id === id) as any;
      if (dev) dev.data.favorite = false;
    }
    dev = this.developmentsFavorites.findIndex((item) => item.id === id) as any;
    if (dev !== -1) {
      this.developmentsFavorites.splice(dev, 1);
      dev = this.developmentsFavoritesSearch.findIndex(
        (item) => item.id === id
      ) as any;
      if (dev !== -1) this.developmentsFavoritesSearch.splice(dev, 1);
    }
    if (this.currentTab === 0) this.developmentsView = this.developmentsSearch;
    else this.developmentsView = this.developmentsFavoritesSearch;
  }

  @action
  async refactorProjectAndOpen(dev): Promise<boolean> {
    this.refactorDevelopName = dev.data.name;
    this.loadingWbsUpdate = true;
    this.layerAdd = true;
    let refactorResult = await DevelopmentService.RefactorAndOpen(dev);
    if (!refactorResult) {
      this.layerAdd = false;
      this.loadingWbsUpdate = false;
    }
    return refactorResult;
  }

  @action
  setDevelopmentCopied(development: FirebaseDocument<Development>) {
    this.fromCopy = development;
    this.isCopy = true;
    this.layerAdd = true;
  }

  @action
  clearDevelopmentCopied() {
    this.fromCopy = undefined;
    this.isCopy = false;
    this.loadingCopy = false;
  }

  @action
  showTrialMessageError(isStorageSpace: boolean = false) {
    this.trialMessageError = isStorageSpace
      ? "out-of-storage-space"
      : "resource-unavailable";
  }

  @action
  clearTrialMessageError() {
    this.trialMessageError = "";
  }

  async sendClickTrackToHubspot() {
    const email = LoginStore.userAuth!.email || "";
    return LoginService.sendClickEventToHubspot(email);
  }
}

const store = new DevelopmentStore();
export { store as DevelopmentStore };



// WEBPACK FOOTER //
// ./src/modules/development/DevelopmentStore.ts